// src/pages/micro-exercises/MicroExerciseCategoryPage.js
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import OuterHeader from "../../components/OuterHeader";
import Footer from "../../components/Footer";
import { MICRO_EXERCISE_CATEGORIES } from "../../data/microExercisesData";
import "../../css2/MicroExerciseCategoryPage.css";

export default function MicroExerciseCategoryPage() {
  // For now, we only have one category – grounding.
  const category = MICRO_EXERCISE_CATEGORIES[0];

  /* ---------------------------------------------
     DYNAMIC SUGGESTIONS (gentle + emotional-safe)
  ---------------------------------------------- */
  const SOFT_SUGGESTIONS = {
    grounding: {
      label: "Emotional First-Aid Toolkit",
      slug: "emotional-first-aid",
      message: "If you want a little more support today:",
    },
  };

  const suggestion = SOFT_SUGGESTIONS[category.id];

  /* ---------------------------------------------
     Compute filters + exercises
  ---------------------------------------------- */
  const subcategoryMeta = useMemo(() => {
    if (!category) return [];
    const counts = {};
    category.exercises.forEach((ex) => {
      counts[ex.subcategoryId] = (counts[ex.subcategoryId] || 0) + 1;
    });
    return category.subcategories.map((sub) => ({
      ...sub,
      count: counts[sub.id] || 0,
    }));
  }, [category]);

  const allExercises = useMemo(() => {
    if (!category) return [];
    const labelMap = {};
    category.subcategories.forEach((sub) => {
      labelMap[sub.id] = sub.label;
    });
    return category.exercises.map((ex) => ({
      ...ex,
      subLabel: labelMap[ex.subcategoryId] || "",
    }));
  }, [category]);

  const [activeFilter, setActiveFilter] = useState("all");

  const filteredExercises = useMemo(() => {
    if (!category) return [];
    if (activeFilter === "all") return allExercises;
    return allExercises.filter((ex) => ex.subcategoryId === activeFilter);
  }, [activeFilter, allExercises, category]);

  const totalCapsules = allExercises.length;

  /* --------------------------------------------- */
  if (!category) {
    return (
      <>
        <OuterHeader />
        <div className="caps-page">
          <div className="caps-wrapper">
            <h2 className="caps-empty-title">Category not found</h2>
            <p className="caps-empty-text">
              This micro-exercise collection doesn’t exist yet.
            </p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  /* --------------------------------------------- */
  return (
    <>
      <OuterHeader />

      <div className="caps-page">
        <div className="caps-wrapper">

          {/* HERO */}
          <section className="caps-hero">
            <div className="caps-hero-text">
              <div className="caps-eyebrow">Capsules of Care</div>
              <h1 className="caps-hero-title">{category.title}</h1>
              <p className="caps-hero-subtitle">{category.subtitle}</p>

              <div className="caps-hero-meta">
                <span>{totalCapsules} capsules</span>
                <span>•</span>
                <span>Most under 2 minutes</span>
              </div>
            </div>

            <div className="caps-hero-orb">
              <div className="caps-hero-orb-inner" />
            </div>
          </section>

          {/* FILTERS */}
          <section className="caps-filter">
            <div className="caps-filter-header">
              <span className="caps-filter-label">Filter by category</span>
            </div>

            <div className="caps-filter-row">
              <button
                type="button"
                className={`caps-chip caps-chip-all ${
                  activeFilter === "all" ? "caps-chip--active" : ""
                }`}
                onClick={() => setActiveFilter("all")}
              >
                <span>All capsules</span>
                <span className="caps-chip-count">{totalCapsules}</span>
              </button>

              {subcategoryMeta.map((sub) => (
                <button
                  type="button"
                  key={sub.id}
                  className={`caps-chip ${
                    activeFilter === sub.id ? "caps-chip--active" : ""
                  }`}
                  onClick={() => setActiveFilter(sub.id)}
                >
                  <span>{sub.label}</span>
                  <span className="caps-chip-count">{sub.count}</span>
                </button>
              ))}
            </div>
          </section>

          {/* CAPSULE GRID */}
          <section className="caps-grid-section">
            <div className="caps-grid">
              {filteredExercises.map((ex) => (
                <Link
                  key={ex.id}
                  to={`/micro-exercises/${ex.id}`}
                  state={{ from: "capsules" }}
                  className="caps-card"
                >
                  <div className="caps-card-top">
                    <span className="caps-tag caps-tag-sub">{ex.subLabel}</span>
                    <span className="caps-tag caps-tag-time">
                      {ex.duration}
                    </span>
                  </div>

                  <h3 className="caps-card-title">{ex.title}</h3>
                  <p className="caps-card-subtitle">{ex.subtitle}</p>

                  <div className="caps-card-footer">
                    <span className="caps-card-cta">Start activity</span>
                    <span className="caps-card-arrow">→</span>
                  </div>
                </Link>
              ))}

              {filteredExercises.length === 0 && (
                <div className="caps-empty-state">
                  <p>No capsules here yet. New activities coming soon ✨</p>
                </div>
              )}
            </div>
          </section>

          {/* ---------------------------------------------------
             GENTLE TOOLKIT SUGGESTION (non-intrusive)
          ------------------------------------------------------ */}
          {suggestion && (
            <div className="caps-soft-link">
              {suggestion.message}{" "}
              <Link to={`/toolkits/${suggestion.slug}`}>
                {suggestion.label} →
              </Link>
            </div>
          )}

        </div>
      </div>

      <Footer />
    </>
  );
}
